import { deepmerge } from '@mui/utils';
import { createTheme } from '@mui/material/styles';
import type { ThemeOptions } from '@mui/material/styles';
import { DEFAULTCOLOR_TEXT_DISABLED } from '../default/palette';
import { generateDefaultThemeOptions } from '../default';
import type { Device } from '../default';
import generateFontFaceDeclarations from '../../utils/generateFontFaceDeclarations';
import type { FontFaceType } from '../../utils/generateFontFaceDeclarations';
import MontserratRegular from './fonts/montserrat-regular.woff';
import MontserratSemiBold from './fonts/montserrat-semibold.woff';
import MontserratBold from './fonts/montserrat-bold.woff';
import MontserratExtraBold from './fonts/montserrat-extrabold.woff';
import MontserratItalic from './fonts/montserrat-italic.woff';

const COLOR_PRIMARY_MAIN = '#fdc300';
const DEFAULTCOLOR_TEXT_DARK = '#000000';
const DEFAULTCOLOR_TEXT_TRANSPARENT_DARK = 'rgba(0, 0, 0, 0.6)';

const FONT_FAMILY = 'Montserrat, Arial, Helvetica, sans-serif';
export const FONTS = {
  Montserrat: {
    400: {
      normal: MontserratRegular,
      italic: MontserratItalic,
    },
    600: MontserratSemiBold,
    700: MontserratBold,
    800: MontserratExtraBold,
  },
} satisfies FontFaceType;
const FONTFACE_DECLARATIONS = generateFontFaceDeclarations(FONTS);

export const FONT_PRELOADS = [FONTS.Montserrat[400].normal, FONTS.Montserrat[700]];

/** Define theme object.
 * See here for more info: https://mui.com/customization/theming/ */
const themeOptions: ThemeOptions = {
  // https://mui.com/customization/palette/
  palette: {
    primary: {
      main: COLOR_PRIMARY_MAIN,
      textTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_DARK,
    },
    secondary: {
      main: '#ef7c00',
    },
    grey: {
      main: '#d5d3d1',
      light: '#f4f4f4',
      dark: '#8c8480',
    },
    text: {
      disabled: DEFAULTCOLOR_TEXT_DISABLED,
      primary: DEFAULTCOLOR_TEXT_DARK,
      secondary: DEFAULTCOLOR_TEXT_TRANSPARENT_DARK,
      dark: DEFAULTCOLOR_TEXT_DARK,
      darkTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_DARK,
    },
  },
  // https://mui.com/customization/typography/
  typography: {
    fontFamily: FONT_FAMILY,
    fontWeightRegular: 400,
    fontWeightSemiBold: 600,
    fontWeightBold: 700,
    fontWeightBlack: 800,
  },
  // https://mui.com/customization/theme-components/
  components: {
    MuiCssBaseline: {
      styleOverrides: FONTFACE_DECLARATIONS,
    },
    MuiButton: {
      styleOverrides: {
        root: ({
          theme: {
            unstable_sx: sx,
            palette: { grey },
          },
        }) =>
          sx({
            ':disabled': { bgcolor: grey.main, color: grey.dark },
          }),
        containedPrimary: ({
          theme: {
            unstable_sx: sx,
            palette: { text, grey },
          },
        }) =>
          sx({
            color: text.dark,
            background: `linear-gradient(${COLOR_PRIMARY_MAIN}, #fdb300 80%)`,
            ':disabled': { background: 'none', bgcolor: grey.main },
            ':hover': {
              background: 'linear-gradient(#fdb400, #fda500 80%)',
            },
          }),
      },
    },
  },
};

/** Merge with default theme options and create a theme instance
 * See here for more info: https://mui.com/customization/theming/#ap i */
const theme = (device: Device) =>
  createTheme(deepmerge(generateDefaultThemeOptions(device), themeOptions));

// eslint-disable-next-line import/no-default-export
export default theme;
